<template lang="pug">
  WtTile.wellnessChallengeQuestionsNotification(v-if="hasWellnessChallengeQuestion" parent vertical)
    WtTile.is-paddingless(child :col="12" v-for="question in activeWellnessChallengeQuestions" :key="question.id")
      BNotification(
        type="is-lightgrey"
        aria-close-label="Close active wellness question"
        v-if="showQuestionNotification(question)"
      )
        span.mr-3 You have an unanswered question in the {{ question.challengeName }} challenge.
        span
          a.has-text-blue(href="#" @click="activeQuestionOpen = true") Answer Now
      BModal(:active.sync="activeQuestionOpen" has-modal-card :key="question.id" @close="modalClosed")
        .modal-card.challenge-questions-container
          .modal-header
            .header-container
              .challenge-questions-header
                span {{challengeQuestionsHeader()}}
            .close-button(@click="modalClosed" aria-label="Close modal")
              span(v-html="require('!!html-loader!@/assets/icons/cross-icon.svg')")
          .modal-body
            .progress-text-container
              span {{getProgressText()}}
            .question-container
              .question-text-container(v-if="question.question")
                span.question-label {{question.question}}
                span.points-inline(v-if="question.pointValue") &nbsp; {{question.pointValue}} pts

              .question-description(v-if="question.description")
                div.editor__content(v-html="question.description")
              QuestionAnswers(
                :question="question"
                @onAnswer="collectAnswer($event)"
                :show-answers="answerSaved"
                :useCustomQAStyles="true"
              )
          .modal-footer.footer-shadow
            .submit-button-container(v-if="!answerSaved")
              WtButton.is-fullwidth(
                @click.native="submitAnswer(question)"
                :disabled="disableSubmit || _.isEmpty(answerData)"
                :loading="answerSaving"
              ) Submit
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtButton from "@@/WtButton/WtButton";
import WtTile from "@@/WtTile/WtTile";
import WtIconButton from "@@/WtIconButton/WtIconButton";
import WtLabel from "@@/WtLabel/WtLabel";
import QuestionAnswers from "@@/ProgramQuestions/QuestionAnswers";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon.vue";

export default {
  name: "WellnessChallengeQuestionsNotification",

  components: {
    WtSvgIcon,
    WtButton,
    WtTile,
    WtIconButton,
    WtLabel,
    QuestionAnswers
  },

  data() {
    return {
      answerData: null,
      activeQuestionOpen: false,
      disableSubmit: true,
      answerQuestionError: false,
      answerQuestionErrorMessage: null,
      answerSaved: false,
      answerSaving: false,
      challengeId: ""
    };
  },

  computed: {
    ...mapGetters("challenges", ["getChallengeById"]),

    hasWellnessChallengeQuestion() {
      return !this._.isEmpty(this.activeWellnessChallengeQuestions);
    },
    activeWellnessChallengeQuestions() {
      if (this.challengeId !== null) {
        const challenge = this.getChallengeById(this.challengeId);

        if (challenge && challenge.questionActive) {
          return [
            {
              challengeId: challenge.id,
              challengeName: challenge.name,
              ...challenge.questionActive
            }
          ];
        }
        return [];
      }

      const userChallenges = this.$auth.user().challenges || [];

      return userChallenges
        .filter(challenge => challenge.isWellness && challenge.questionActive)
        .map(challenge => ({
          challengeId: challenge.id,
          challengeName: challenge.name,
          ...challenge.questionActive
        }));
    }
  },

  watch: {
    answerData(newValue) {
      if (!this._.isEmpty(newValue)) {
        this.disableSubmit = false;
      }
    },

    activeWellnessChallengeQuestions(newValue, oldValue) {
      // if there are multiple questions (currently business logic is to only allow for one wellness challenge but during testing there might be multiple :)
      // the api currently will only return the unanswered question so after submitting an answer we refresh that data via this.$auth.fetch()
      if (
        newValue.length < oldValue.length &&
        this.$route.name === "MyDashboard"
      ) {
        this.resetInitialState();
      }
    }
  },

  methods: {
    ...mapActions(["snackBar"]),
    ...mapActions("members", ["fetchMember"]),

    resetInitialState() {
      this.answerData = null;
      this.activeQuestionOpen = false;
      this.disableSubmit = true;
      this.answerQuestionError = false;
      this.answerQuestionErrorMessage = null;
      this.answerSaved = false;
      this.answerSaving = false;
    },

    collectAnswer($event) {
      this.answerData = $event;
    },

    async submitAnswer(question) {
      try {
        this.answerSaving = true;
        const response = await this.axios.post(
          `challenges/${question.challengeId}/answer-question`,
          {
            memberId: this.$auth.user().id,
            questionId: this.answerData.questionId,
            questionAnswer: this.answerData.answer
          }
        );
        this.answerSaved = !this._.isEmpty(response);
        this.snackBar({ message: "Tracking added!" });
        this.answerSaving = false;
        this.disableSubmit = true;
        this.$emit("refreshPoints");
      } catch (error) {
        this.answerQuestionError = true;
        this.answerQuestionErrorMessage = "";
      }
    },

    modalClosed() {
      this.activeQuestionOpen = false;
      this.$auth.fetch(); // refresh the active wellness questions
      this.fetchMember({
        memberId: this.$auth.user().id,
        setMember: true
      });
      this.challengeId = ""; // reset challengeId
    },

    showQuestionNotification(question) {
      if (this.$route.name === "Challenge") {
        // this.$route.params.challengeId can be a string
        if (this.$route.params.challengeId != question.challengeId)
          return false;
      }
      return true;
    },

    // This function has to be invoked from Parent Modal.
    openQuestionModal(challengeId) {
      this.activeQuestionOpen = true;
      this.challengeId = challengeId.toString();
    },

    getProgressText() {
      const count = this.activeWellnessChallengeQuestions.length;
      return `Complete ${
        count === 1 ? "this question" : "these questions"
      } to progress:`;
    },

    challengeQuestionsHeader() {
      const count = this.activeWellnessChallengeQuestions.length;
      return count === 1 ? "Challenge Question" : "Challenge Questions";
    }
  }
};
</script>

<style lang="sass" scoped>
.modal-card
  max-height: 80vh
.modal-body
  display: flex
  gap: 20px
  flex-direction: column
  flex: 1
  overflow-y: auto
.challenge-questions-container
  display: flex
  flex-direction: column
  gap: 20px
  background-color: white
  border-radius: 16px
.header-container
  padding: 20px 16px
  border-bottom: 1px solid #E8EBEF
  display: flex
  align-items: center
  justify-content: center
  position: relative

.close-button
  position: absolute
  top: 12px
  right: 12px

.progress-text-container
  padding: 0px 16px
  font-weight: 400
  font-size: 14px
  line-height: 20px
  letter-spacing: 0px
  color: #5D6472


.question-container
  display: flex
  gap: 12px
  padding: 0px 16px
  flex-direction: column

.question-label
  font-weight: 700
  font-size: 14px
  line-height: 20px
  letter-spacing: 0px
  color: #1B1D20

.points-inline
  background-color: #78C98B
  color: white
  margin-left: 8px
  padding: 3px
  border-radius: 6px
  font-size: 10px
  font-weight: 700

.question-description
  font-weight: 400
  font-size: 14px
  line-height: 20px
  letter-spacing: 0px
  color: #1B1D20

.footer-shadow
  box-shadow: 0 0 10px rgba(27,29,32, 0.05)

.submit-button-container
  padding: 16px

@media screen and (max-width: 768px)
  .modal-card.challenge-questions-container
    max-width: calc(100% - 32px)
    margin: 0px 16px !important
</style>
